// src/prototype/hooks/useHimssAssessments.ts
// Client-level HIMSS assessment records (D6, D7). Demo store: localStorage, cleared by the
// presenter reset (key is in DEMO_STATE_KEYS). The real store is a client-level table that
// Rahul names; this hook is the only reader/writer so the UI does not change when it moves.
import { useCallback, useEffect, useState } from 'react';
import { emptyHimss, type HimssAssessments } from '../data/himss/himssAssessments';

export const HIMSS_KEY = 'dva_demo_himss_assessments';
const CHANGE_EVENT = 'dva_demo_himss_change';

function read(): HimssAssessments {
  if (typeof window === 'undefined') return emptyHimss();
  try {
    const raw = localStorage.getItem(HIMSS_KEY);
    if (!raw) return emptyHimss();
    const parsed = JSON.parse(raw) as Partial<HimssAssessments>;
    const base = emptyHimss();
    return {
      noneHeld: Boolean(parsed.noneHeld),
      models: { ...base.models, ...(parsed.models ?? {}) },
      dhi: { ...base.dhi, ...(parsed.dhi ?? {}), dimensions: { ...base.dhi.dimensions, ...(parsed.dhi?.dimensions ?? {}) } },
    };
  } catch {
    return emptyHimss();
  }
}

export function useHimssAssessments() {
  const [state, setState] = useState<HimssAssessments>(() => read());

  useEffect(() => {
    const sync = () => setState(read());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('ea_demo_reset', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('ea_demo_reset', sync);
    };
  }, []);

  const update = useCallback((fn: (prev: HimssAssessments) => HimssAssessments) => {
    const next = fn(read());
    try { localStorage.setItem(HIMSS_KEY, JSON.stringify(next)); } catch { /* storage unavailable: keep in memory */ }
    setState(next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }, []);

  return { himss: state, update };
}

export default useHimssAssessments;
